import React, {
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Pressable, View } from 'react-native';
import { theme } from '@garnish/constants';

import { webOnlyStyles } from '../../utils';
import { focusOnElement } from '../../utils/accessibility/focus/focusOnElement.web';
import { CollapseIndicatorIcon } from '../Collapsible';
import { FadeView } from '../FadeView';
import { LoadingPlaceholderFitToView } from '../LoadingPlaceholder';
import { BodyText } from '../Text';
import { styles } from './Select.styles';
import type { TriggerProps, TriggerSelectedOptionProps } from './Select.types';

export const Trigger = forwardRef<View, TriggerProps>((props, ref) => {
  const {
    selectedOption,
    opened,
    accessibilityLabel,
    selectId,
    variation,
    isInteracting,
    isLoading,
    onLayout,
    onPress,
  } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [isFirstPress, setFirstPress] = useState(true);

  // ─── Styles ──────────────────────────────────────────────────────────

  const triggerStyles = [
    styles.row,
    styles.trigger,
    variation === 'box' ? styles.triggerBox : undefined,
  ];
  const hoverContainerStyles =
    variation === 'box' ? styles.triggerHoverBox : styles.triggerHover;

  const iconSize = variation === 'box' ? 16 : 24;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleTrigger = useCallback(() => {
    setFirstPress(false);
    onPress();
  }, [setFirstPress, onPress]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (opened || isFirstPress) return;
    focusOnElement(ref as RefObject<View>);
  }, [ref, isFirstPress, opened]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.triggerWrapper}>
      <FadeView show={isInteracting} style={hoverContainerStyles} />

      {isLoading ? <LoadingPlaceholderFitToView palette="cream" /> : null}

      <Pressable
        ref={ref}
        style={triggerStyles}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onLayout={onLayout}
        onPress={handleTrigger}
        {...{
          accessibilityExpanded: opened,
          accessibilityControls: selectId,
        }}
      >
        <TriggerSelectedOption
          selectedOption={selectedOption}
          bold={variation !== 'box'}
        />

        <CollapseIndicatorIcon
          color={theme.colors.TEXT_COLOR}
          expanded={opened}
          width={iconSize}
          height={iconSize}
        />
      </Pressable>
    </View>
  );
});

// ─── Sub Components ──────────────────────────────────────────────────────────

const TriggerSelectedOption = (props: TriggerSelectedOptionProps) => {
  const { bold, selectedOption = '' } = props;

  return (
    <BodyText
      size={4}
      bold={bold}
      style={webOnlyStyles({ userSelect: 'none' })}
    >
      {selectedOption}
    </BodyText>
  );
};
